import type { LegacyParamRule } from "@/hooks/useLegacySearchParams";
import type { LotExpiryFilter } from "./inventory-labels";

/**
 * Addresses INTO the Stok screens — one place, so a link cannot drift from the screen it opens.
 *
 * WHY THIS FILE EXISTS. Every Stok tab stacks several screens (`SectionStack`), and each block
 * keeps its URL state under its own name: the documents block reads `documents.panel` and
 * `documents.record`, not `panel` and `record`. The links written before that existed —
 * `/inventory/documents?record=…` from the code lookup, `/inventory/products?record=…` — were
 * redirected to the right tab and then opened nothing (warehouse-manager review, 7 Oct 2026).
 *
 * The block names below ARE the namespaces: `InventorySectionPage` and `StockStatusPage` build
 * their stacks from these constants, so renaming a block renames every link to it.
 */
export const INVENTORY_BLOCKS = {
  balances: "balances",
  reservations: "reservations",
  ledger: "ledger",
  products: "products",
  lots: "lots",
  serials: "serials",
  documents: "documents",
  /** satis-duzeltme 20 Ekim 2026: «Sevk bekleyen siparişler» — onay bildirimi buraya götürüyor. */
  shipments: "shipments",
  counts: "counts",
  reorderRules: "reorder-rules",
  its: "its",
  warehouses: "warehouses",
  locations: "locations",
  categories: "categories",
  barcodes: "barcodes",
  units: "units",
  conversions: "conversions",
} as const;

/**
 * The blocks' names before 31 October 2026 — Turkish, against the address rule (UI/UX review
 * K-A5). An address that still carries one (a bookmark, a notification the server sent with
 * `sevk.panel=…` before it switched to `shipments.`) is rewritten to the new name before the
 * section draws (`AdminSection` › `legacyBlocks`). The bridge stays: notifications already in
 * people's inboxes keep the old link for as long as they are kept.
 */
export const LEGACY_INVENTORY_BLOCKS: readonly LegacyParamRule[] = [
  { from: "bakiyeler", to: "balances" },
  { from: "rezervasyonlar", to: "reservations" },
  { from: "defter", to: "ledger" },
  { from: "urunler", to: "products" },
  { from: "partiler", to: "lots" },
  { from: "seriler", to: "serials" },
  { from: "belgeler", to: "documents" },
  { from: "sevk", to: "shipments" },
  { from: "sayim", to: "counts" },
  { from: "siparis-noktasi", to: "reorder-rules" },
  { from: "depolar", to: "warehouses" },
  { from: "raflar", to: "locations" },
  { from: "kategoriler", to: "categories" },
  { from: "barkodlar", to: "barcodes" },
  { from: "birimler", to: "units" },
  { from: "donusumler", to: "conversions" },
];

/**
 * The id the «Stok Defteri» block carries. A link that filters the ledger also asks for it with
 * this hash, and the ledger brings itself into view — it is the third block of its tab, and a
 * filter applied below the fold reads as "nothing happened".
 */
export const LEDGER_ANCHOR = "stok-defteri";

/** The id «Partiler» carries, for the same reason: it is the second block of «Ürünler». */
export const LOTS_ANCHOR = "partiler";

/** The id «Sipariş Noktası» carries: the third block of «Depo İşlemleri» (17 Ekim 2026). */
export const REORDER_ANCHOR = "siparis-noktasi";

type Query = Record<string, string | null | undefined>;

/** `block.key=value` pairs for one block, in a stable order; empty values are left out. */
export function blockQuery(block: string, values: Query): URLSearchParams {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value) query.set(`${block}.${key}`, value);
  }
  return query;
}

function withQuery(path: string, query: URLSearchParams, hash?: string): string {
  const text = query.toString();
  return `${path}${text ? `?${text}` : ""}${hash ? `#${hash}` : ""}`;
}

/** Opens one stock document's drawer in «Depo İşlemleri». The drawer reads it by id. */
export function documentHref(documentId: string): string {
  return withQuery(
    "/inventory/movements",
    blockQuery(INVENTORY_BLOCKS.documents, { panel: "edit", record: documentId }),
  );
}

/** Opens one count's card in «Depo İşlemleri» — the «Sayım» block reads it by id (17 Ekim 2026). */
export function countHref(countId: string): string {
  return withQuery(
    "/inventory/movements",
    blockQuery(INVENTORY_BLOCKS.counts, { panel: "edit", record: countId }),
  );
}

/** The count-entry screen — counting in front of the shelf (17 Ekim 2026). */
export function countEntryHref(countId: string): string {
  return `/inventory/counts/${countId}/entry`;
}

/**
 * Opens a product card in «Ürünler». The list is narrowed to the product's code as well: the
 * drawer edits a row of the CURRENT page, and a product on page four would otherwise open an
 * empty form.
 */
export function productHref(product: { id: string; code: string }): string {
  return withQuery(
    "/inventory/products",
    blockQuery(INVENTORY_BLOCKS.products, { q: product.code, panel: "edit", record: product.id }),
  );
}

/**
 * «Stok Bakiyeleri» searched for one product by its code: which warehouse, which shelf, how much
 * (depo turu, 9 Ekim 2026, üçüncü tur). The search box shows the code, so the reader sees why the
 * list is narrowed and clears it the usual way. The code, not the id: the balances block has no
 * product filter of its own, and its search matches `product.code`.
 */
export function balancesHref(productCode: string): string {
  return withQuery("/inventory/stock", blockQuery(INVENTORY_BLOCKS.balances, { q: productCode }));
}

/** What a link may narrow the ledger by. The keys are the ledger endpoint's own filters. */
export interface LedgerLinkFilters {
  productId?: string | null | undefined;
  lotId?: string | null | undefined;
  warehouseId?: string | null | undefined;
  locationId?: string | null | undefined;
  stockStatus?: string | null | undefined;
}

export const LEDGER_LINK_KEYS = [
  "productId",
  "lotId",
  "warehouseId",
  "locationId",
  "stockStatus",
] as const;

/** «Stok Defteri» narrowed to the given product / lot / place, scrolled into view. */
export function ledgerHref(filters: LedgerLinkFilters): string {
  const values: Query = {};
  for (const key of LEDGER_LINK_KEYS) values[key] = filters[key];
  return withQuery("/inventory/stock", blockQuery(INVENTORY_BLOCKS.ledger, values), LEDGER_ANCHOR);
}

/**
 * The same ledger filter applied to the CURRENT address — for a link from a block on the same
 * tab. Everything else in the address survives (the company, the other blocks' state), the
 * ledger's paging restarts, and `close` names keys to drop (the drawer the link sits in).
 */
export function ledgerSearchFrom(
  current: URLSearchParams,
  filters: LedgerLinkFilters,
  close: readonly string[] = [],
): string {
  const next = new URLSearchParams(current);
  for (const key of close) next.delete(key);
  const block = INVENTORY_BLOCKS.ledger;
  for (const key of [...LEDGER_LINK_KEYS, "cursor", "movementType", "from", "to"]) {
    next.delete(`${block}.${key}`);
  }
  for (const [key, value] of blockQuery(block, { ...filters })) next.set(key, value);
  return next.toString();
}

/**
 * «Partiler» filtered by expiry, scrolled into view. `expiry=90` is «90 gün içinde dolacak»:
 * `/inventory/products?lots.expiry=90#partiler` — the address Panelim's «Miadı yaklaşan»
 * opens. Without the anchor the link landed on «Ürünler» at the top of the tab.
 *
 * `inStock` is «Yalnız stoklu» (17 Ekim 2026, gece raporu #28): Panelim counts lots that still
 * hold stock, and the list it opens has to show the same set — not the emptied lots as well.
 */
export function lotsHref(filters: {
  expiry?: LotExpiryFilter;
  productId?: string;
  inStock?: boolean;
}): string {
  return withQuery(
    "/inventory/products",
    blockQuery(INVENTORY_BLOCKS.lots, {
      expiry: filters.expiry,
      productId: filters.productId,
      inStock: filters.inStock ? "1" : undefined,
    }),
    LOTS_ANCHOR,
  );
}

/** The id «Rezervasyonlar» carries: the second block of «Stok Durumu», below the balances. */
export const RESERVATIONS_ANCHOR = "rezervasyonlar";

/**
 * «Rezervasyonlar» searched for one sales document's holds — every state, not only the open ones
 * (17 Ekim 2026). The order card links here: a confirmed order's holds are OPEN, a cancelled
 * order's are RELEASED, and both are the answer to «what did this order hold». The search box
 * shows the number, so the reader sees why the list is narrowed; the reservation row carries it
 * (`sourceDocumentNo`).
 */
export function reservationsHref(documentNo: string): string {
  const query = blockQuery(INVENTORY_BLOCKS.reservations, { q: documentNo });
  // `state` empty = «Tümü». Written explicitly: the block's default is «Açık».
  query.set(`${INVENTORY_BLOCKS.reservations}.state`, "");
  return withQuery("/inventory/stock", query, RESERVATIONS_ANCHOR);
}

/**
 * «Sipariş Noktası» narrowed to «Yalnız kritik seviyenin altındakiler» and scrolled into view
 * (17 Ekim 2026, gece raporu #20): the address Panelim's «Kritik seviyenin altında» opens.
 */
export function reorderRulesHref(filters: { belowMin?: boolean }): string {
  return withQuery(
    "/inventory/movements",
    blockQuery(INVENTORY_BLOCKS.reorderRules, { belowMin: filters.belowMin ? "1" : undefined }),
    REORDER_ANCHOR,
  );
}
